import { ListItem, Stack, Box, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, FormGroup, TextField, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { FC, ReactElement, useEffect } from 'react';
import { KeywordRule } from '@/gen/api/securefile';
import StringArrayTextField from '@/components/input/StringArrayTextField';
import { usePlainStateFormUtils } from '@/components/input/usePlainStateFormUtils';
import { useZSliceUnderContext } from '@/utils/zustand-form/hooks';
import { CollectEditsProtocol, CollectEditsMetadata } from '@/utils/zustand-form/ext/collectEdits';
import { krSchema } from './InfoTypeEditor.types';
import type { FormToken } from '@/utils/zustand-form/types';


const KeywordRuleLine : FC<{
  parentToken: FormToken,
  className?: string,
  r_key: string,
  prevData: KeywordRule,
  actionButton: ReactElement,
}> = ({ parentToken, className, r_key, prevData, actionButton }) => {
  const zustandUtils = useZSliceUnderContext<KeywordRule>({
    parentToken: parentToken ?? null,
    pathFragment: r_key,
    prevData: prevData,
    stageInit: () => ({ ...prevData }),
  });
  const { states, setStates, installProtocolMetadata } = zustandUtils
  
  // When using zustand-form, sync prevData changes into the zustand slice
  useEffect(() => {
    setStates(v => ({...v, prevData, stage: {...prevData}, errors: {}, touched: {} }));
  }, [prevData, setStates]);
  
  const { trySubmitValidate, fieldProps } = usePlainStateFormUtils(states, setStates, krSchema);
  useEffect(() => {
    return installProtocolMetadata<CollectEditsMetadata>(CollectEditsProtocol.metadataKey, {
      triggerValidate: async () => {
        const v1 = await trySubmitValidate();
        if (!v1.errors['regex'] && !v1.errors['words']) {
          const isUsingWords = v1.stage.regex == null;
          if (isUsingWords) {
            if (!v1.stage.words || v1.stage.words.length === 0) {
              v1.errors['words'] = 'Context Keywords is required';
              v1.touched['words'] = true;
            }
          } else {
            if (!v1.stage.regex) {
              v1.errors['regex'] = 'Context Pattern is required';
              v1.touched['regex'] = true;
            }
            v1.stage.words = null;
          }
          setStates({...v1});
        }
        if (Object.keys(v1.errors).length > 0) {
          throw new Error('Validation error', { cause: v1.errors });
        }
      }
    })
  }, [installProtocolMetadata, setStates, trySubmitValidate])

  const isUsingWords = (x: {regex?: string|null|undefined}) => x.regex == null;
  const radioValue = isUsingWords(states.stage) ? 'words' : 'regex';
  const radioHandleChange = (e:any, value:string) => {
    if (value === radioValue) { return }
    if (value === 'words') {
      setStates(v => {
        v.stage.regex = null;
        v.stage.words = [...(v.prevData.words ??[])];
        delete v.errors['regex'];
        delete v.touched['regex'];
        (v.touched as any)['kind'] = !isUsingWords(v.prevData)
        return {...v}
      })
    } else {
      setStates(v => {
        v.stage.words = null;
        v.stage.regex = v.prevData.regex ?? '';
        delete v.errors['words'];
        delete v.touched['words'];
        (v.touched as any)['kind'] = isUsingWords(v.prevData)
        return {...v}
      })
    }
  }
  return (
    <ListItem className={className ?? (Object.values(states.touched).some(x => x) ? 'dirty' : undefined)}>
      <Stack direction="row" flexWrap="wrap" alignItems="center" width="100%" marginBottom={2}>
        <FormControl sx={{ mr: 'auto'}} size="small">
          <FormLabel>Type</FormLabel>
          <RadioGroup row value={radioValue} onChange={radioHandleChange}>
            <FormControlLabel value="words" label="Context Keywords" control={<Radio />} />
            <FormControlLabel value="regex" label="Context Pattern" control={<Radio />} />
          </RadioGroup>
        </FormControl>
        {actionButton}
        <Box width="100%" marginBottom={2}>
          {isUsingWords(states.stage)
            ? <StringArrayTextField list={states.stage.words ?? []} stackChips="row"
                setList={(ls) => setStates(v => {
                  const words = [...new Set(ls)]
                  const v1 = ({...v,
                    stage: {...v.stage, words},
                    touched: {...v.touched, words: true},
                    ...(words.length === 0 ? {errors: {...v.errors, words: 'Context Keywords are required'}} : (delete v.errors['words'], {errors: {...v.errors}}))
                  })
                  return v1
                })}
                placeholder="Add a new value (case insensitive) and press the Enter key to confirm..."
                error={Boolean(states.touched['words'] && states.errors['words'])}
                helperText={states.touched['words'] && states.errors['words']}
              />
            : <TextField fullWidth multiline {...fieldProps('regex')} placeholder="Type Regular Expression in here..." />}
        </Box>
        <Box marginRight={8}>
          <FormControl>
            <FormLabel sx={{mb:0.5, display: 'flex', flexDirection: 'row', alignItems: 'start'}}>
              <span>Context Window</span>
              <Tooltip title="A context window designates the number of characters before and after the matched content discovered in Pattern Detection.">
                <Box role="button">
                  <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
                </Box>
              </Tooltip>
            </FormLabel>
            <FormGroup row>
              <TextField variant="standard" label="Before" sx={{ width: 80, mr: 2 }} size="small" type="number" {...fieldProps('windowBefore', (x: any) => {
                if (!x) { return undefined }
                let n = +x
                if (Number.isNaN(n)) { return 0 }
                n = Math.floor(n)
                return (n < 0 ? 0 : n)
              })} />
              <TextField variant="standard" label="After" sx={{ width: 80 }} size="small" type="number" {...fieldProps('windowAfter', (x: any) => {
                if (!x) { return undefined }
                let n = +x
                if (Number.isNaN(n)) { return 0 }
                n = Math.floor(n)
                return (n < 0 ? 0 : n)
              })} />
            </FormGroup>
          </FormControl>
        </Box>
        <TextField variant="standard" label="Context Score" sx={{ width: 140, alignSelf: 'end' }} type="number"
          {...fieldProps('scoreAdjustment', (x: any) => {
            if (!x) { return undefined }
            let n = +x
            if (Number.isNaN(n)) { return 0 }
            n = parseFloat(n.toFixed(2))
            return (
                n < 0 ? 0
              : n > 1 ? 1
              : n)
          })}
          inputProps={{ step: 0.01, max: 1, min: 0, }}
        />
      </Stack>
    </ListItem>
  )
}

export default KeywordRuleLine;
